import React from 'react';
import { Box, styled, Typography } from '@mui/material';
import {
  PATHOLOGY_REPORT_HEADING_PREFIX,
  isPathologyReportHeading
} from '@oh-vcp/components-common';
import { HtmlContent } from '@oh-vcp/components-ui';
import EconsultLabResultLines from '../econsultLabResultLines/EconsultLabResultLines';

const PreformattedText = styled(Typography)({
  whiteSpace: 'pre-wrap',
  overflowWrap: 'break-word',
  fontFamily: 'Consolas, "Courier New", monospace'
});

const SECTION_HEADING = /^[A-Z][A-Z /-]{1,39}$/;
const HTML_BODY = /^\s*</;

export type PathologyReportSegment = {
  heading?: string;
  lines: string[];
};

export const toPathologyBlocks = (content?: string): string[] =>
  content ? content.split('\n\n').filter((block) => block.trim()) : [];

const headingOf = (block: string) => block.split('\n')[0].trim();

const bodyOf = (block: string) => block.split('\n').slice(1).join('\n');

export const toPathologySegments = (body: string): PathologyReportSegment[] => {
  const segments: PathologyReportSegment[] = [];
  let current: PathologyReportSegment = { lines: [] };

  const flush = () => {
    if (current.heading || current.lines.some((line) => line.trim())) {
      segments.push(current);
    }
  };

  body.split('\n').forEach((line) => {
    if (SECTION_HEADING.test(line)) {
      flush();
      current = { heading: line, lines: [] };
    } else {
      current.lines.push(line);
    }
  });
  flush();

  return segments;
};

const EconsultPathologyReports: React.FC<{ content: string }> = ({ content }) => (
  <>
    {toPathologyBlocks(content).map((block, blockIndex) => {
      const heading = headingOf(block);
      const body = bodyOf(block);

      if (!isPathologyReportHeading(heading)) {
        return <EconsultLabResultLines key={`${blockIndex}`} content={body} />;
      }

      const label = heading.slice(PATHOLOGY_REPORT_HEADING_PREFIX.length).trim();

      return (
        <Box key={`${blockIndex}`} mt={blockIndex === 0 ? 0 : 3}>
          {label && (
            <Typography variant="body2" color="base.grey3" fontWeight="bold" mb={1}>
              {label}
            </Typography>
          )}
          {HTML_BODY.test(body) ? (
            <Typography variant="body2" color="base.grey3" component="div">
              <HtmlContent htmlString={body} component="div" />
            </Typography>
          ) : (
            toPathologySegments(body).map((segment, segmentIndex) => (
              <Box key={`${segmentIndex}`} mt={segmentIndex === 0 ? 0 : 2}>
                {segment.heading && (
                  <PreformattedText variant="body2" color="base.grey4" fontWeight="bold">
                    {segment.heading}
                  </PreformattedText>
                )}
                {segment.lines.length > 0 && (
                  <PreformattedText variant="body2" color="base.grey3">
                    {segment.lines.join('\n')}
                  </PreformattedText>
                )}
              </Box>
            ))
          )}
        </Box>
      );
    })}
  </>
);

export default EconsultPathologyReports;
